// The RFQ letter's values for the live preview.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/rfqLetter.ts
//
// The same rules as the backend's rfq-letter.util.ts, which fills these at
// send time. The composer recomputes them as the buyer edits lines and terms,
// so the preview is the email that goes out. Change both together.

export interface LetterProfile {
  companyName?: string;
  buyer?: { name?: string; title?: string; phone?: string };
  shipTo?: { name?: string; street?: string; city?: string; postalCode?: string; country?: string };
  timeZone: string;
  currency: string;
  incoterm: string;
  incotermPlace?: string;
  paymentTerms: string;
  quoteValidityDays: number;
}

export interface LetterTerms {
  currency?: string;
  incoterms?: string;
  paymentTerms?: string;
  validityDays?: number;
  replyByDate?: string;
}

export interface LetterPart {
  manufacturer?: string;
  mpn?: string;
  partNumber?: string;
  quantity?: string;
}

/** Values the system writes into the letter: shown, not edited as wording. */
export const LETTER_FIELDS = ['shipToCountry', 'deliveryLine', 'termsLine', 'replyByLong', 'replyByShort', 'subjectItem', 'multiple', 'documentsLine'];

export function unitWord(unit: string | undefined, qty: number): string {
  const u = (unit || 'EA').toUpperCase();
  if (['EA', 'PC', 'PCS', 'EACH', 'PIECE'].includes(u)) return qty === 1 ? 'pc' : 'pcs';
  return u.toLowerCase();
}

export function quantityText(qty: number, unit?: string): string {
  return `${qty} ${unitWord(unit, qty)}`;
}

function deliveryLine(profile: LetterProfile, incoterm?: string): string {
  const shipTo = profile.shipTo ?? {};
  const where = [shipTo.name || profile.companyName, shipTo.street, [shipTo.postalCode, shipTo.city].filter(Boolean).join(' '), shipTo.country]
    .filter(Boolean)
    .join(', ');
  const term = incoterm || profile.incoterm;
  const place = profile.incotermPlace || shipTo.city || shipTo.country;
  if (term === 'EXW') return `${where}. EXW your site, with freight to ${shipTo.country || 'us'} shown separately.`;
  return `${where}. ${term}${place ? ` ${place}` : ''} please, or EXW with freight shown separately.`;
}

function termsLine(profile: LetterProfile, terms: LetterTerms): string {
  const days = typeof terms.validityDays === 'number' && terms.validityDays > 0 ? terms.validityDays : profile.quoteValidityDays;
  return `${terms.currency || profile.currency}, ${terms.paymentTerms || profile.paymentTerms}, quote valid ${days} days`;
}

function replyByText(value: string | undefined, timeZone: string): { long: string; short: string } {
  if (!value) return { long: '', short: '' };
  const bare = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = new Date(bare ? `${value}T12:00:00Z` : value);
  if (isNaN(d.getTime())) return { long: '', short: '' };
  const tz = bare ? 'UTC' : timeZone;
  return {
    long: bare
      ? d.toLocaleDateString('en-GB', { timeZone: tz, weekday: 'long', day: 'numeric', month: 'long' })
      : d.toLocaleString('en-GB', { timeZone: tz, weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }),
    short: d.toLocaleDateString('en-GB', { timeZone: tz, day: 'numeric', month: 'short' }),
  };
}

function subjectItem(parts: LetterPart[]): string {
  if (parts.length !== 1) return `${parts.length} items`;
  const p = parts[0];
  const item = [p.manufacturer, p.mpn || p.partNumber].filter(Boolean).join(' ');
  return p.quantity ? `${item} x ${p.quantity}` : item;
}

export function letterVariables(profile: LetterProfile, parts: LetterPart[], terms: LetterTerms = {}): Record<string, string> {
  const country = profile.shipTo?.country ?? '';
  const many = parts.length > 1;
  const reply = replyByText(terms.replyByDate, profile.timeZone);
  return {
    shipToCountry: country,
    deliveryLine: deliveryLine(profile, terms.incoterms),
    termsLine: termsLine(profile, terms),
    introLine: `Please quote the item${many ? 's' : ''} below${country ? ` for delivery to ${country}` : ''}.`,
    replyByLong: reply.long,
    replyByShort: reply.short,
    subjectItem: subjectItem(parts),
    multiple: many ? 'yes' : '',
    senderName: profile.buyer?.name ?? '',
    senderTitle: profile.buyer?.title ?? '',
    senderPhone: profile.buyer?.phone ?? '',
    companyName: profile.companyName ?? '',
  };
}

/**
 * The letter's values for lines that ask by requirements (mirror of the backend's briefVariables): whether to
 * ask which requirements the model meets, and what the customer asked to receive with the quote.
 */
export function briefVariables(parts: Array<{ brief?: { asks?: string[] } | null }>): { anyBrief: string; briefAsks: string } {
  const briefs = parts.map((p) => p.brief).filter((b): b is { asks?: string[] } => !!b);
  const asks: string[] = [];
  for (const b of briefs) for (const a of b.asks ?? []) if (!asks.some((x) => x.toLowerCase() === a.toLowerCase())) asks.push(a);
  return { anyBrief: briefs.length ? 'yes' : '', briefAsks: asks.join(', ') };
}
